import { X, ChevronDown, ChevronUp } from 'lucide-react'
import useFilterStore from '@/store/useFilterStore'
import { useFilterOptions } from '@/hooks/useFilterOptions'
import { useDashboardData } from '@/hooks/useDashboardData'
import { usePrefectureDashboardData } from '@/hooks/usePrefectureDashboardData'
import { useState } from 'react'

const ActiveFiltersBar = () => {
  const { data: filterOptions } = useFilterOptions()
  const { mutate: mutateDashboard } = useDashboardData()
  const { mutate: mutatePrefecture } = usePrefectureDashboardData()
  const [isExpanded, setIsExpanded] = useState(true)
  
  const {
    selectedCategory,
    startYear,
    endYear,
    selectedPrefectures,
    selectedTeams,
    selectedGeneration,
    achievementType,
    setSelectedCategory,
    setStartYear,
    setEndYear,
    setSelectedPrefectures,
    setSelectedTeams,
    setSelectedGeneration,
    resetFilters,
  } = useFilterStore()

  // アクティブなフィルターの数をカウント
  const activeFilterCount = [
    selectedCategory,
    startYear,
    endYear,
    selectedPrefectures.length > 0,
    selectedTeams.length > 0,
    selectedGeneration,
  ].filter(Boolean).length

  // フィルターが1つも選択されていない場合は非表示
  if (activeFilterCount === 0) {
    return null
  }

  // カテゴリ名を取得
  const categoryName = selectedCategory
    ? filterOptions?.categories?.find(cat => cat.id === selectedCategory)?.name
    : null

  // 都道府県名を取得
  const prefectureNames = selectedPrefectures
    .map(id => filterOptions?.prefectures?.find(p => p.id === id)?.name)
    .filter(Boolean) as string[]

  // チーム名を取得
  const teamNames = selectedTeams
    .map(id => filterOptions?.teams?.find(t => t.id === id)?.name)
    .filter(Boolean) as string[]

  // 世代名を取得
  const generationName = selectedGeneration
    ? filterOptions?.generations?.find(g => g.id === selectedGeneration)?.name
    : null

  // 年度範囲の表示
  const yearRange = startYear && endYear
    ? `${startYear}年 - ${endYear}年`
    : startYear
    ? `${startYear}年以降`
    : endYear
    ? `${endYear}年以前`
    : null

  // フィルターを削除する関数
  const removeFilter = (type: string, value?: any) => {
    switch (type) {
      case 'category':
        setSelectedCategory(null)
        break
      case 'startYear':
        setStartYear(null)
        break
      case 'endYear':
        setEndYear(null)
        break
      case 'prefecture':
        setSelectedPrefectures(selectedPrefectures.filter(id => id !== value))
        break
      case 'team':
        setSelectedTeams(selectedTeams.filter(id => id !== value))
        break
      case 'generation':
        setSelectedGeneration(null)
        break
    }
    mutateDashboard()
    mutatePrefecture()
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg mb-4 sm:mb-6 lg:mb-8">
      <div className="p-3 sm:p-4">
        {/* ヘッダー */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-sm sm:text-base font-semibold text-gray-900">
              適用中のフィルター ({activeFilterCount}件)
            </span>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-blue-600 hover:text-blue-800 transition-colors"
              aria-label={isExpanded ? '折りたたむ' : '展開する'}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </div>
          <button
            onClick={() => {
              resetFilters()
              mutateDashboard()
              mutatePrefecture()
            }}
            className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            すべてクリア
          </button>
        </div>

        {/* フィルター一覧 */}
        {isExpanded && (
          <div className="flex flex-wrap gap-2 mt-3">
            {/* カテゴリ */}
            {categoryName && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800 border border-blue-300">
                <span>カテゴリ: {categoryName}</span>
                <button
                  onClick={() => removeFilter('category')}
                  className="ml-1 hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                  aria-label="カテゴリを削除"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {/* 年度範囲 */}
            {yearRange && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium bg-purple-100 text-purple-800 border border-purple-300">
                <span>年度: {yearRange}</span>
                <button
                  onClick={() => {
                    removeFilter('startYear')
                    removeFilter('endYear')
                  }}
                  className="ml-1 hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                  aria-label="年度を削除"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {/* 都道府県 */}
            {prefectureNames.map((name, index) => {
              const prefectureId = selectedPrefectures[index]
              return (
                <span
                  key={prefectureId}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium bg-green-100 text-green-800 border border-green-300"
                >
                  <span>都道府県: {name}</span>
                  <button
                    onClick={() => removeFilter('prefecture', prefectureId)}
                    className="ml-1 hover:bg-green-200 rounded-full p-0.5 transition-colors"
                    aria-label={`${name}を削除`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )
            })}

            {/* チーム */}
            {teamNames.map((name, index) => {
              const teamId = selectedTeams[index]
              return (
                <span
                  key={teamId}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium bg-orange-100 text-orange-800 border border-orange-300"
                >
                  <span>チーム: {name}</span>
                  <button
                    onClick={() => removeFilter('team', teamId)}
                    className="ml-1 hover:bg-orange-200 rounded-full p-0.5 transition-colors"
                    aria-label={`${name}を削除`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )
            })}

            {/* 世代 */}
            {generationName && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium bg-pink-100 text-pink-800 border border-pink-300">
                <span>世代: {generationName}</span>
                <button
                  onClick={() => removeFilter('generation')}
                  className="ml-1 hover:bg-pink-200 rounded-full p-0.5 transition-colors"
                  aria-label="世代を削除"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ActiveFiltersBar

